/**
 * Reading Firebase auth failures: which ones mean the stored session is dead,
 * and what to say to a player about the rest.
 *
 * Pure and kept out of `net/firebase.ts` so both questions are testable without
 * the SDK, a popup, or a live emulator.
 */
import { FirebaseError } from "firebase/app";

/**
 * Codes that mean the server will no longer vouch for the session this device
 * is holding: the account behind it is gone, disabled, or its tokens have been
 * revoked.
 *
 * The distinction that matters is against `auth/network-request-failed`, which
 * is deliberately **not** here. "I could not reach the server" and "the server
 * disowned this account" are indistinguishable from the client's point of view
 * if you squint, and treating the first as the second would sign people out
 * every time their train went into a tunnel.
 */
const DEAD_SESSION_CODES = new Set([
  "auth/invalid-refresh-token",
  "auth/invalid-user-token",
  "auth/user-token-expired",
  "auth/user-not-found",
  "auth/user-disabled",
]);

/** Whether this error means the locally stored session is no longer usable. */
export function isDeadSessionError(err: unknown): boolean {
  return err instanceof FirebaseError && DEAD_SESSION_CODES.has(err.code);
}

/**
 * What to show the player, or null to show nothing at all.
 *
 * Null is for the cancellations: closing the Google window is a decision, not a
 * failure, and painting a red line under a button someone deliberately backed
 * out of is the app arguing with them.
 *
 * Anything that isn't a Firebase auth error keeps its own message. Callers use
 * this on join/host too, where the interesting failures come from our own
 * server ("that code doesn't match a game") and are already written for people.
 */
export function authErrorMessage(err: unknown): string | null {
  if (!(err instanceof FirebaseError)) {
    if (err instanceof Error && err.message) return err.message;
    return err ? String(err) : "Something went wrong — please try again";
  }
  switch (err.code) {
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
    case "auth/user-cancelled":
      return null;
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window — allow pop-ups and try again";
    case "auth/network-request-failed":
      return "Couldn't reach the server — check your connection and try again";
    case "auth/too-many-requests":
      return "Too many attempts — wait a moment and try again";
    case "auth/user-disabled":
      return "That account has been disabled";
    default:
      if (DEAD_SESSION_CODES.has(err.code)) {
        // Recovered from automatically (see signInWithGoogle), so this is the
        // wording for the case where the retry also failed.
        return "Your previous session expired — try signing in again";
      }
      return "Sign-in failed — please try again";
  }
}
